import { createGlobalStore } from 'hox';
import { useCallback, useMemo, useState } from 'react';

import { ServiceNameEnum } from '../types';
import { isServiceName, SERVICE_TYPES } from '../utils/service';

export type ServiceSwitches = {
  [key in ServiceNameEnum]?: boolean;
};

function useDashboardServices() {
  const [services, setServices] = useState<ServiceSwitches>({});

  const setAllServicesSelectedStatus = useCallback(
    (names: ServiceNameEnum[], enable: boolean) => {
      const _services: ServiceSwitches = {};
      for (const name of names) {
        if (isServiceName(name)) {
          _services[name] = enable;
        }
      }
      setServices(_services);
    },
    []
  );

  const updateServiceSelectedStatus = useCallback(
    (name: ServiceNameEnum, enable: boolean) => {
      if (isServiceName(name)) {
        const newServices = { ...services };
        newServices[name] = enable;
        setServices({ ...newServices });
        localStorage.setItem(
          'casCurSelectedServices',
          JSON.stringify({ ...newServices })
        );
      }
    },
    [services]
  );

  const serviceCount = useMemo(() => {
    return Object.entries(services).length;
  }, [services]);

  const enableCount = useMemo(() => {
    return Object.entries(services).filter(([, enable]) => enable).length;
  }, [services]);

  /**
   * If there are no provisioned services, no request will be sent.
   * Therefore, add the condition enableCount > 0 here.
   */
  const hasEnabledProvisionedServices = useMemo(() => {
    return serviceCount > 0 && enableCount > 0;
  }, [serviceCount, enableCount]);

  const enabledServices = useMemo(() => {
    return SERVICE_TYPES.filter((x) => services[x.name])
      .sort((a, b) => a.order - b.order)
      .map((x) => x.name);
  }, [services]);

  return {
    services,
    setAllServicesSelectedStatus,
    hasEnabledProvisionedServices,
    updateServiceSelectedStatus,
    serviceCount,
    enableCount,
    enabledServices,
  };
}

export const [useDashboardServicesStore] =
  createGlobalStore(useDashboardServices);
